import { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { AdminPage } from "@/pages/admin";
import { DebugIndexPage, DebugDbPage } from "@/pages/debug";
import { RequireAdminAuthOutlet } from "@/app/outlets/AdminOutlet";
import { RequireDebugModeOutlet } from "@/app/outlets/RequireDebugModeOutlet";
import { TrashPage } from "@/pages/trash";
import { CollectionsListPage } from "@/pages/collections";
import { CollectionsViewPage } from "@/pages/collections-view";
import { SearchPage } from "@/pages/search";
import { NotFoundPage } from "@/pages/not-found";
import { appRoutes } from "@/app/router/appRoutes";
import { RequireStandardModeOutlet } from "@/app/outlets/RequireStandardModeOutlet";
import { RequireStandardModeOverlayOutlet } from "@/app/outlets/RequireStandardModeOverlayOutlet";
import { StandardModePadding } from "@/app/outlets/StandardModePadding";
import { NotesListPage } from "@/pages/notes/notes-list-page";
import { SettingsAccountMigrationPage } from "@/pages/settings/settings-account-migration-page";
import { V1MigrationPage } from "@/pages/v1-migration/v1-migration-page";
import { SessionsListPage } from "@/pages/sessions-list";
import { SessionsViewPage } from "@/pages/sessions-view";
import {
  AudioRecordingsPage,
  AudioRecordingDetailPage,
  AudioRecordingAdminPage,
  AudioRecordingCreatePage,
  AudioRecordingActiveStatePage,
} from "@/pages/audio-recordings";
import { IntegrationsTextMessagePage } from "@/pages/integrations/text-message/integrations-text-message-page";
import { OAuthConsentPage } from "@/pages/oauth-consent/oauth-consent-page";
import { IntegrationsSlackCallbackPage } from "@/pages/integrations/slack/integrations-slack-callback-page";
import {
  IntegrationsCalendarGoogleOAuthCallbackPage,
  IntegrationsCalendarOutlookOAuthCallbackPage,
  IntegrationsCalendarPage,
} from "@/pages/integrations/calendar";
import { FeatureEnrollmentPage } from "@/pages/feature-enrollment";
import { ImportedNotesListPage } from "@/pages/notes/imported-notes-list-page";
import { ImportedCollectionsListPage } from "@/pages/collections/imported-collections-list-page";
import { StandardNotesViewPage } from "@/pages/notes-view/notes-view-page";
import { SmartViewPage } from "@/smart-views/pages/SmartViewPage";
import { SmartViewsListPage } from "@/smart-views/pages/SmartViewsListPage";
import { SettingsRedirect } from "@/app/router/SettingsRedirect";
import { SettingsTabId } from "@/store/pages/SettingsPageStore/types";
import { AgentRedirect } from "@/app/router/agent-redirect";
import { AgentSurfaceId } from "@/store/agent-overlay/types";

const STANDARD_MODE_APP_SHELL_CHILD_ROUTES: RouteObject[] = [
  /**
   * =============================================
   *               V1 Migrated Routes
   * =============================================
   */
  {
    path: appRoutes.v1MigrationGroups({ starPattern: true }).path,
    element: <V1MigrationPage />,
  },
  {
    path: appRoutes.v1MigrationNotes({ starPattern: true }).path,
    element: <V1MigrationPage />,
  },
  {
    path: appRoutes.v1MigrationTemplates({ starPattern: true }).path,
    element: <V1MigrationPage />,
  },
  /**
   * =============================================
   *               Notes Routes
   * =============================================
   */
  {
    path: appRoutes.notesView({
      params: { noteId: ":noteId" },
    }).path,
    element: <StandardNotesViewPage />,
  },
  {
    path: appRoutes.notesList({}).path,
    element: <NotesListPage />,
  },
  {
    path: appRoutes.home({}).path,
    element: <Navigate to={appRoutes.notesList({}).path} />,
  },
  {
    path: appRoutes.everything({}).path,
    element: <Navigate to={appRoutes.notesList({}).path} />,
  },
  /**
   * =============================================
   *               Agent Overlay Routes
   * =============================================
   */
  {
    path: appRoutes.agent({}).path,
    element: <AgentRedirect surfaceId={AgentSurfaceId.Overview} />,
  },
  {
    path: appRoutes.projects({}).path,
    element: <AgentRedirect surfaceId={AgentSurfaceId.Projects} />,
  },
  {
    path: appRoutes.project({
      params: { projectId: ":projectId" },
    }).path,
    element: <AgentRedirect surfaceId={AgentSurfaceId.Project} routeParamName="projectId" />,
  },
  {
    path: appRoutes.tasks({}).path,
    element: <AgentRedirect surfaceId={AgentSurfaceId.Tasks} />,
  },
  {
    path: appRoutes.task({
      params: { taskId: ":taskId" },
    }).path,
    element: <AgentRedirect surfaceId={AgentSurfaceId.Task} routeParamName="taskId" />,
  },
  {
    path: appRoutes.activities({}).path,
    element: <AgentRedirect surfaceId={AgentSurfaceId.Activities} />,
  },
  {
    path: appRoutes.activity({
      params: { activityId: ":activityId" },
    }).path,
    element: <AgentRedirect surfaceId={AgentSurfaceId.Activity} routeParamName="activityId" />,
  },
  /**
   * =============================================
   *               Import Routes
   * =============================================
   */
  {
    path: appRoutes.importBatchNotes({
      params: { importBatchId: ":importBatchId" },
    }).path,
    element: <ImportedNotesListPage />,
  },
  {
    path: appRoutes.importBatchCollections({
      params: { importBatchId: ":importBatchId" },
    }).path,
    element: <ImportedCollectionsListPage />,
  },
  /**
   * =============================================
   *               Trash Routes
   * =============================================
   */
  {
    path: appRoutes.trash({}).path,
    element: <TrashPage />,
  },
  /**
   * =============================================
   *               Session Routes
   * =============================================
   */
  {
    path: appRoutes.sessionsList({}).path,
    element: <SessionsListPage />,
  },
  {
    path: appRoutes.sessionsView({
      params: { sessionId: ":sessionId" },
    }).path,
    element: <SessionsViewPage />,
  },
  {
    path: appRoutes.audioRecordings({}).path,
    element: <AudioRecordingsPage />,
  },
  {
    path: appRoutes.audioRecordingActiveState({}).path,
    element: <AudioRecordingActiveStatePage />,
  },
  {
    path: appRoutes.audioRecordingCreate({}).path,
    element: <AudioRecordingCreatePage />,
  },
  {
    path: appRoutes.audioRecording({
      params: { recordingId: ":recordingId" },
    }).path,
    element: <AudioRecordingDetailPage />,
  },
  {
    path: appRoutes.audioRecordingAdmin({
      params: { recordingId: ":recordingId" },
    }).path,
    element: <AudioRecordingAdminPage />,
  },
  /**
   * =============================================
   *               Settings Routes
   * =============================================
   */
  {
    path: appRoutes.settingsAccountMigration({}).path,
    element: <SettingsAccountMigrationPage />,
  },
  /**
   * =============================================
   *               Smart View Routes
   * =============================================
   */
  {
    path: appRoutes.smartViews({}).path,
    element: <SmartViewsListPage />,
  },
  {
    path: appRoutes.smartViewsView({
      params: { smartViewId: ":smartViewId" },
    }).path,
    element: <SmartViewPage />,
  },
  /**
   * =============================================
   *               Collections Routes
   * =============================================
   */
  {
    path: appRoutes.collections({}).path,
    element: <CollectionsListPage />,
  },
  {
    path: appRoutes.collectionsView({
      params: { collectionId: ":collectionId" },
    }).path,
    element: <CollectionsViewPage />,
  },
  /**
   * =============================================
   *               Integrations Routes
   * =============================================
   */
  {
    path: appRoutes.integrationsSlack({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.MemAgentPage} />,
  },
  {
    path: appRoutes.integrationsSlackCallback({}).path,
    element: <IntegrationsSlackCallbackPage />,
  },
  {
    path: appRoutes.integrationsGoogleCalendar({}).path,
    element: <IntegrationsCalendarPage />,
  },
  {
    path: appRoutes.integrationsGoogleCalendarCallback({}).path,
    element: <IntegrationsCalendarGoogleOAuthCallbackPage />,
  },
  {
    path: appRoutes.integrationsOutlookCalendarCallback({}).path,
    element: <IntegrationsCalendarOutlookOAuthCallbackPage />,
  },
  {
    path: appRoutes.integrationsTextMessage({}).path,
    element: <IntegrationsTextMessagePage />,
  },
  /**
   * =============================================
   *               Search Routes
   * =============================================
   */
  {
    path: appRoutes.search({}).path,
    element: <SearchPage />,
  },
  /**
   * =============================================
   *               Admin Routes
   * =============================================
   */
  {
    path: appRoutes.admin({}).path,
    element: <RequireAdminAuthOutlet />,
    children: [
      {
        path: appRoutes.admin({}).path,
        element: <AdminPage />,
      },
    ],
  },
  {
    path: appRoutes.debug({}).path,
    element: <RequireDebugModeOutlet />,
    children: [
      {
        path: appRoutes.debug({}).path,
        element: <DebugIndexPage />,
      },
      {
        path: appRoutes.debugDb({}).path,
        element: <DebugDbPage />,
      },
    ],
  },
  /**
   * =============================================
   *    Settings & Integrations Redirects
   *    (old route-based pages → settings modal)
   * =============================================
   */
  {
    path: appRoutes.settings({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.NotificationPreferencesPage} />,
  },
  {
    path: appRoutes.settingsNotificationPreferences({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.NotificationPreferencesPage} />,
  },
  {
    path: appRoutes.settingsEmailPreferences({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.NotificationPreferencesPage} />,
  },
  {
    path: appRoutes.settingsVoiceMode({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.VoiceModePage} />,
  },
  {
    path: appRoutes.settingsChat({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.ChatPage} />,
  },
  {
    path: appRoutes.settingsMemAgent({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.MemAgentPage} />,
  },
  {
    path: appRoutes.settingsSubscriptionManagement({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.SubscriptionManagementPage} />,
  },
  {
    path: appRoutes.settingsReferralProgram({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.ReferralProgramPage} />,
  },
  {
    path: appRoutes.settingsAccountManagement({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.ProfilePage} />,
  },
  {
    path: appRoutes.settingsExports({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.ExportsPage} />,
  },
  {
    path: appRoutes.settingsDebug({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.DebugAccountPage} />,
  },
  {
    path: appRoutes.settingsDebugAccount({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.DebugAccountPage} />,
  },
  {
    path: appRoutes.settingsDebugSandbox({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.DebugSandboxPage} />,
  },
  {
    path: appRoutes.integrations({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.CalendarAndMeetingsPage} />,
  },
  {
    path: appRoutes.integrationsZapier({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.ZapierPage} />,
  },
  {
    path: appRoutes.integrationsApi({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.ApiKeysPage} />,
  },
  {
    path: appRoutes.integrationsEmail({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.EmailToMemPage} />,
  },
  {
    path: appRoutes.integrationsChromeExtension({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.ChromeExtensionPage} />,
  },
  {
    path: appRoutes.integrationsMcp({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.McpPage} />,
  },
  {
    path: appRoutes.integrationsImports({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.ImportsPage} />,
  },
  {
    path: appRoutes.templatesList({}).path,
    element: <SettingsRedirect tabId={SettingsTabId.TemplatesPage} />,
  },
];

export const STANDARD_MODE_APP_SHELL_CONTENT_ROUTES: RouteObject[] = [
  {
    path: appRoutes.root({}).path,
    element: <RequireStandardModeOutlet />,
    children: STANDARD_MODE_APP_SHELL_CHILD_ROUTES,
  },
];

export const STANDARD_MODE_APP_SHELL_ROUTES: RouteObject[] = [
  ...STANDARD_MODE_APP_SHELL_CONTENT_ROUTES,
  {
    path: appRoutes.root({ starPattern: true }).path,
    element: (
      // Apply consistent padding for the NotFoundPage.
      <StandardModePadding>
        <NotFoundPage />
      </StandardModePadding>
    ),
  },
];

// Standalone authenticated flows belong outside the standard app shell so they do not inherit
// global overlays like onboarding, top-level modals, or app chrome.
export const STANDARD_MODE_STANDALONE_ROUTES: RouteObject[] = [
  {
    path: appRoutes.oauthConsent({}).path,
    element: <RequireStandardModeOverlayOutlet />,
    children: [
      {
        index: true,
        element: <OAuthConsentPage />,
      },
    ],
  },
  {
    path: appRoutes.featureEnrollment({ params: { slug: ":slug" } }).path,
    element: <RequireStandardModeOverlayOutlet />,
    children: [
      {
        index: true,
        element: <FeatureEnrollmentPage />,
      },
    ],
  },
];
